/**
 * ControlX Activity Feed — wire types.
 *
 * Mirrors the ControlX "Activity Feed & Live Stream API" contract (RCS screen
 * integration) one-to-one. Everything except `id` is optional on purpose: the
 * spec says payload keys vary slightly by producer and that `detail` changes
 * shape per category, so the types describe what may arrive rather than what we
 * hope arrives. Consumers must render defensively.
 */

/** Severity ladder used to colour/annotate a feed row. Cancels arrive as `warning`. */
export type ControlxSeverity = 'info' | 'success' | 'warning' | 'error' | 'critical';

/** Producers a frame can originate from. */
export type ControlxSource = 'adloggs' | 'shipsy' | 'wheelseye' | 'trip';

export const CONTROLX_SEVERITIES: readonly ControlxSeverity[] = [
    'info', 'success', 'warning', 'error', 'critical'
] as const;

/**
 * ControlX timestamps are IST-naive (no timezone suffix) and must be read as
 * Asia/Kolkata regardless of where the browser is. This is a property of the
 * API, not of the tenant, so it is fixed here rather than taken from the
 * env-driven `constant.utcToLocalTime` (which is +04:00 on the UAE build).
 */
export const CONTROLX_TZ_OFFSET = '+05:30';

/** Default page size for the REST snapshot, per the spec. */
export const CONTROLX_DEFAULT_LIMIT = 50;

/** One entry in the activity feed. */
export interface ControlxActivity {
    /** `db_<activity_id>` — the dedupe key shared by REST and SSE. */
    id: string;
    activity_id?: number | null;
    /** IST-naive datetime, e.g. `2026-09-01T14:22:08`. */
    timestamp?: string | null;
    /** Short agent/domain code the entry was filed under. */
    prefix?: string | null;
    /** e.g. `agent_decision`. `react_reasoning` is excluded from the feed server-side. */
    category?: string | null;
    severity?: ControlxSeverity | string | null;
    order_id?: string | number | null;
    trip_id?: string | number | null;
    tenant_user_id?: number | null;
    branch_id?: number | null;
    source?: ControlxSource | string | null;
    rider_id?: string | number | null;
    agent_name?: string | null;
    /** Human-readable line for the feed (<= 500 chars). */
    message?: string | null;
    /** Structured context; shape varies by category. */
    detail?: unknown;
    /** Icon hint, e.g. `cpu`, `info`. */
    icon?: string | null;
    related_decision_id?: string | null;
    related_usage_id?: number | null;
    /** Present on stream frames (`"activity"`); absent on REST rows. */
    type?: string | null;
}

/**
 * Returned only when the snapshot was requested with `order_id`.
 *
 * A healthy on-track order has zero visible events by design, so this block is
 * what lets an empty timeline say "ControlX is watching, last checked HH:MM"
 * instead of the (wrong) "not monitored".
 */
export interface ControlxWatching {
    checks?: number | null;
    last_checked_at?: string | null;
}

/** Raw REST envelope. */
export interface ControlxActivityResponse {
    success?: boolean;
    data?: ControlxActivity[];
    watching?: ControlxWatching | null;
}

/** Normalised REST result handed to callers. */
export interface ControlxActivitySnapshot {
    entries: ControlxActivity[];
    watching: ControlxWatching | null;
}

export interface ControlxActivityQuery {
    orderId?: string | number | null;
    tripId?: string | number | null;
    limit?: number;
    prefix?: string | null;
}

export interface ControlxStreamQuery {
    orderId?: string | number | null;
    tripId?: string | number | null;
    source?: ControlxSource | string | null;
}

/**
 * Payload of the named `re-assign` / `Cancel` frames. Shape is fixed by the
 * spec; the hyphenated key is the API's, not ours.
 */
export interface ControlxActionPayload {
    order_id?: string | number | null;
    reason?: string | null;
    'action-for'?: string | null;
}

export type ControlxActionKind = 'reassign' | 'cancel';

/** Everything the live stream can hand a subscriber. */
export type ControlxStreamEvent =
    /** Connection is up. `reconnected` is true for every open after the first. */
    | { kind: 'open'; reconnected: boolean }
    /** The browser dropped the connection and is retrying on its own. */
    | { kind: 'reconnecting' }
    | { kind: 'activity'; entry: ControlxActivity }
    | { kind: 'action'; action: ControlxActionKind; payload: ControlxActionPayload };

/**
 * Why a ControlX call failed, in the terms the UI cares about.
 *
 * `not-found` is the spec's deliberate answer for an order the caller may not
 * see (404 rather than 403, so order ids cannot be probed) — it is a
 * permission outcome, not a missing-record one.
 */
export type ControlxErrorKind =
    | 'unauthorized'   // 401 — token missing/expired
    | 'forbidden'      // 403 — ControlX addon not activated for this account
    | 'not-found'      // 404 — order not visible to this caller
    | 'network'        // transport failure, CORS, stream dropped for good
    | 'unknown';       // anything else, including a malformed envelope

export interface ControlxError {
    kind: ControlxErrorKind;
    status: number;
    message: string;
}

/** True when the failure can only repeat — never retry or open a stream after one. */
export function isControlxTerminalError(kind: ControlxErrorKind | null | undefined): boolean {
    return kind === 'unauthorized' || kind === 'forbidden' || kind === 'not-found';
}

export function controlxError(kind: ControlxErrorKind, status: number, message: string): ControlxError {
    return { kind, status, message };
}
